import {createContext, use, useMemo, type ComponentProps, type ComponentType, type PropsWithChildren} from 'react'

export type LayoutLinkProps = Omit<ComponentProps<'a'>, 'href'> & {
  to: string
  preventAutofocus?: boolean
}

function AnchorLink({to, children, preventAutofocus: _preventAutofocus, ...props}: LayoutLinkProps) {
  return (
    <a href={to} {...props}>
      {children}
    </a>
  )
}

export const LayoutLinkContext = createContext<{Link: ComponentType<LayoutLinkProps>}>({Link: AnchorLink})

export const LayoutBehaviorContext = createContext({
  sharedCookieEnabled: true,
  footerOffsetEnabled: false,
})

export interface LayoutPrimitivesProviderProps {
  /** Adapt the host router to the layout's `to` API. Defaults to native anchor navigation. */
  linkComponent?: ComponentType<LayoutLinkProps>
  /** Keep the legacy cookie format for hosts that have not migrated to keyed preferences. */
  sharedCookieEnabled?: boolean
  /** Apply the alternate footer offset used by the host's page chrome. */
  footerOffsetEnabled?: boolean
}

export function LayoutPrimitivesProvider({
  linkComponent = AnchorLink,
  sharedCookieEnabled = true,
  footerOffsetEnabled = false,
  children,
}: PropsWithChildren<LayoutPrimitivesProviderProps>) {
  const links = useMemo(() => ({Link: linkComponent}), [linkComponent])
  const behavior = useMemo(
    () => ({sharedCookieEnabled, footerOffsetEnabled}),
    [sharedCookieEnabled, footerOffsetEnabled],
  )
  return (
    <LayoutBehaviorContext value={behavior}>
      <LayoutLinkContext value={links}>{children}</LayoutLinkContext>
    </LayoutBehaviorContext>
  )
}

export function LayoutLink(props: LayoutLinkProps) {
  const {Link} = use(LayoutLinkContext)
  return <Link {...props} />
}
